import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCallout,
  CFormLabel,
  CFormSelect,
  CSmartTable,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import EmployerStatusBand from "../../components/employer/EmployerStatusBand";
import { useEmployerTrainingSession } from "../../components/employer/EmployerModeContext";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import {
  buildCandidateCaseRows,
  buildGateSummary,
  buildAnalyticsSnapshot,
  formatDateTime,
} from "../../services/employer/employerCaseOsDomain";
import { formatEmployerAdFocusLabel } from "../../utils/employerAdLabels";
import {
  buildEmployerTrustOpsSummary,
  buildEmployerTrustSummary,
  getPublishingRightsStageColor,
  getRiskLevelColor,
  getTrustTierColor,
} from "../../services/employer/employerTrustDomain";

export default function EmployerWorkspacePage() {
  const navigate = useNavigate();
  const { resumeTraining } = useEmployerTrainingSession();
  const workspace = useEmployerOsWorkspace({ preloadDetails: true, preloadFiling: true });
  const gateSummary = useMemo(() => buildGateSummary(workspace.profile || {}), [workspace.profile]);
  const trustSummary = useMemo(() => buildEmployerTrustSummary(workspace.profile || {}), [workspace.profile]);
  const trustOpsSummary = useMemo(() => buildEmployerTrustOpsSummary(workspace.profile || {}), [workspace.profile]);
  const caseRows = useMemo(
    () => buildCandidateCaseRows({
      ads: workspace.ads,
      applications: workspace.applications,
      detailsById: workspace.detailsById,
      filingById: workspace.filingById,
      gateSummary,
    }),
    [gateSummary, workspace.ads, workspace.applications, workspace.detailsById, workspace.filingById]
  );
  const analyticsSnapshot = useMemo(
    () => buildAnalyticsSnapshot({
      ads: workspace.ads,
      caseRows,
      documentRows: [],
      profile: workspace.profile || {},
    }),
    [caseRows, workspace.ads, workspace.profile]
  );

  const statusItems = [
    {
      label: "Employer Gate",
      value: gateSummary.statusLabel,
      note: `${gateSummary.readyCount}/${gateSummary.totalCount} verbindliche Gate-Bereiche sind dokumentiert.`,
      tone: gateSummary.status === "complete" ? "success" : gateSummary.status === "review" ? "warning" : undefined,
    },
    {
      label: "Anzeigen",
      value: `${analyticsSnapshot.activeAds}/${analyticsSnapshot.totalAds}`,
      note: "Aktive oder veroeffentlichte Anzeigen gegen Gesamtbestand.",
    },
    {
      label: "Cases",
      value: `${analyticsSnapshot.filingReadyCases}/${analyticsSnapshot.totalCases}`,
      note: analyticsSnapshot.blockerCases
        ? `${analyticsSnapshot.blockerCases} Cases blockieren aktuell wegen Pflichtunterlagen, unklarer Pruefung oder fehlender Freigaben.`
        : "Keine harten Filing-Blocker im aktuell geladenen Bestand.",
    },
    {
      label: "Naechste Fuehrung",
      value: !trustSummary.canPublishLive ? "Trust-Blocker haerten" : analyticsSnapshot.filingReadyCases ? "filing-ready Cases entscheiden" : "Pflichtunterlagen und Freigaben haerten",
      note: !trustSummary.canPublishLive
        ? trustSummary.publishBlockers[0] || trustSummary.nextTrustAction
        : analyticsSnapshot.filingReadyCases
          ? `${analyticsSnapshot.filingReadyCases} Cases sind aktuell filing-ready.`
          : "Ohne filing-ready Cases fuehrt der Arbeitsraum zuerst ueber Pflichtunterlagen, Consent und Pruefstatus.",
    },
  ];

  const queueItems = caseRows.map((entry) => ({
    id: entry.id,
    candidate: entry.candidateRef,
    job: entry.adTitle,
    caseStatus: entry.caseStatusLabel,
    caseStatusColor: entry.caseStatusColor,
    documents: `${entry.documentsReady}/${entry.mandatoryRequirementCount}`,
    consents: entry.consentGapCount ? `${entry.consentGapCount} offen` : "vollstaendig",
    score: `${entry.scorecard.percent}%`,
    nextAction: entry.nextAction,
    updatedAt: formatDateTime(entry.updatedAt),
    raw: entry,
  }));

  return (
    <EmployerPageFrame
      title="Arbeitsraum"
      subtitle="Der Employer-Bereich fuehrt jetzt als Employer Case OS ueber Gate, Jobs, Cases, Dokumente, Filing und die naechste reale Aktion."
      actions={(
        <>
          {workspace.currentRun ? (
            <CButton color="secondary" variant="outline" onClick={resumeTraining}>
              Training fortsetzen
            </CButton>
          ) : null}
          <CButton color="secondary" variant="outline" onClick={() => navigate("/dashboard/employer/profile")}>
            Arbeitgeberprofil
          </CButton>
          <CButton color="primary" onClick={() => navigate("/dashboard/employer/jobs/wizard")}>
            Anzeige anlegen
          </CButton>
        </>
      )}
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Case OS",
        shellDescription: "Jobs, Kandidaten, Dokumente, Filing und Integration folgen derselben operativen Produktgrammatik.",
        topContext: "Arbeitsraum",
      }}
    >
      {workspace.error ? (
        <CCallout color="danger">
          <strong>Arbeitsraum konnte nicht vollstaendig geladen werden.</strong>
          <div>{workspace.error}</div>
        </CCallout>
      ) : null}

      <CCallout color={trustSummary.canPublishLive ? "success" : "warning"}>
        <strong>{trustSummary.trustStatusLabel}</strong>
        <div>{trustSummary.canPublishLive ? "Der Employer ist als Direktarbeitgeber verifiziert. Der Arbeitsraum fuehrt jetzt ueber reale Cases und Anzeigen." : trustSummary.publishBlockers[0] || trustSummary.nextTrustAction}</div>
      </CCallout>

      <EmployerStatusBand
        kicker="Employer Case OS"
        title="Ein Fuehrungsraum statt verteilter Einzel-Screens"
        note="Der Arbeitsraum zeigt zuerst Gate-Reife, aktive Cases und die naechste zulaessige Bewegung. Jobs, Kandidaten, Dokumente und Filing sind nur verschiedene Lesarten desselben Betriebssystems."
        items={statusItems}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)]">
        <section className="dashboard-card-shell space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="dashboard-kicker">Case Queue</span>
              <h2 className="dashboard-card-title">Fuehrende Kandidaten- und Case-Queue</h2>
              <p className="dashboard-section-copy">Diese Queue beantwortet zuerst, welcher reale Fall Arbeitgeberzeit verdient und welche Folgeflaeche fachlich als Nächstes richtig ist.</p>
            </div>
            <div className="min-w-[260px]">
              <CFormLabel>Anzeige im Fokus</CFormLabel>
              <CFormSelect
                value={workspace.selectedAdId}
                onChange={(event) => workspace.setSelectedAdId(event.target.value)}
                options={(workspace.ads || []).map((entry) => ({
                  label: formatEmployerAdFocusLabel(entry),
                  value: String(entry.id),
                }))}
              />
            </div>
          </div>

          {queueItems.length ? (
            <CSmartTable
              columns={[
                { key: "candidate", label: "Candidate" },
                { key: "job", label: "Job" },
                { key: "caseStatus", label: "Case-Status" },
                { key: "documents", label: "Pflichtunterlagen" },
                { key: "consents", label: "Freigaben" },
                { key: "score", label: "Scorecard" },
                { key: "nextAction", label: "Naechste Aktion" },
                { key: "updatedAt", label: "Aktualisiert" },
                { key: "actions", label: "Aktion", sorter: false, filter: false },
              ]}
              items={queueItems}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
              scopedColumns={{
                caseStatus: (item) => (
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <CBadge color={item.caseStatusColor}>{item.caseStatus}</CBadge>
                      <small className="text-slate-500">{item.raw.filingStage}</small>
                    </div>
                  </td>
                ),
                documents: (item) => (
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <strong>{item.documents}</strong>
                      <small className="text-slate-500">
                        {item.raw.missingRequirementCount + item.raw.insufficientRequirementCount + item.raw.expiredRequirementCount} kritisch offen
                      </small>
                    </div>
                  </td>
                ),
                consents: (item) => (
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <strong>{item.consents}</strong>
                      <small className="text-slate-500">{item.raw.visibleOriginalDocuments} Originale sichtbar</small>
                    </div>
                  </td>
                ),
                score: (item) => (
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <strong>{item.score}</strong>
                      <small className="text-slate-500">{item.raw.scorecard.bandLabel}</small>
                    </div>
                  </td>
                ),
                actions: (item) => (
                  <td>
                    <div className="d-flex flex-wrap gap-2">
                      <CButton color="secondary" size="sm" variant="outline" onClick={() => navigate(`/dashboard/employer/candidates?adId=${item.raw.adId}&applicationId=${item.raw.applicationId}${workspace.isTraining ? "&training=1" : ""}`)}>
                        Case oeffnen
                      </CButton>
                      <CButton color="secondary" size="sm" variant="outline" onClick={() => navigate(`/dashboard/employer/documents?adId=${item.raw.adId}&applicationId=${item.raw.applicationId}${workspace.isTraining ? "&training=1" : ""}`)}>
                        Dokumente
                      </CButton>
                    </div>
                  </td>
                ),
              }}
            />
          ) : (
            <CCallout color="info">
              <strong>Keine Cases im aktuellen Fokus.</strong>
              <div>Waehle eine andere Anzeige oder beginne im Anzeigenbereich mit einer neuen Anzeige.</div>
            </CCallout>
          )}
        </section>

        <aside className="d-grid gap-4">
          <section className="dashboard-card-shell space-y-3">
            <div>
              <span className="dashboard-kicker">Gate</span>
              <h2 className="dashboard-card-title">Arbeitgeber-Freischaltung</h2>
            </div>
            {(gateSummary.items || []).map((item) => (
              <div key={item.key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="d-flex justify-content-between gap-3">
                  <strong className="text-slate-900">{item.label}</strong>
                  <span className={`rounded-full border px-3 py-1 text-xs ${item.ready ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                    {item.ready ? "Bereit" : "Offen"}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">{item.note}</p>
              </div>
            ))}
            <CButton color="primary" variant="outline" onClick={() => navigate("/dashboard/employer/profile")}>
              Arbeitgeberprofil pflegen
            </CButton>
          </section>

          <section className="dashboard-card-shell space-y-3">
            <div>
              <span className="dashboard-kicker">Trust Operations</span>
              <h2 className="dashboard-card-title">Trust operativ steuern</h2>
            </div>
            <CCallout color={trustOpsSummary.underInvestigation ? "danger" : trustOpsSummary.blockingTasks || trustOpsSummary.activeIncidents ? "warning" : "success"}>
              <strong>{trustOpsSummary.reviewWorkflowLabel}</strong>
              <div>{trustOpsSummary.nextAction}</div>
            </CCallout>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="d-flex justify-content-between gap-3">
                <strong className="text-slate-900">Review-Tasks</strong>
                <span className="text-sm text-slate-500">{trustOpsSummary.blockingTasks} blockierend</span>
              </div>
              <p className="mt-2 mb-0 text-sm text-slate-600">{trustOpsSummary.openTasks} offen, {trustOpsSummary.completedTasks} abgeschlossen.</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="d-flex justify-content-between gap-3">
                <strong className="text-slate-900">Incidents</strong>
                <span className="text-sm text-slate-500">{trustOpsSummary.activeIncidents} aktiv</span>
              </div>
              <p className="mt-2 mb-0 text-sm text-slate-600">
                {trustOpsSummary.criticalIncidents
                  ? `${trustOpsSummary.criticalIncidents} kritische Incidents halten den Employer unter Untersuchung.`
                  : "Keine kritischen Incidents im aktuellen Trust-Fokus."}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="d-flex flex-wrap gap-2">
                <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.trustTierLabel}</CBadge>
                <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
                <CBadge color={getPublishingRightsStageColor(trustOpsSummary.publishingRightsStage)}>{trustOpsSummary.publishingRightsStageLabel}</CBadge>
              </div>
              <p className="mt-2 mb-0 text-sm text-slate-600">
                {trustOpsSummary.employerClassificationLabel} · GPT {(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}% Risiko · Behavior {(trustOpsSummary.behaviorRiskScore * 100).toFixed(0)}%
              </p>
            </div>
            <CButton color="secondary" variant="outline" onClick={() => navigate("/dashboard/employer/profile")}>
              Trust im Profil steuern
            </CButton>
          </section>

          <section className="dashboard-card-shell space-y-3">
            <div>
              <span className="dashboard-kicker">Orchestrierung</span>
              <h2 className="dashboard-card-title">Naechste Systembewegung</h2>
            </div>
            <CCallout color={analyticsSnapshot.blockerCases ? "warning" : "success"}>
              <strong>{analyticsSnapshot.blockerCases ? "Blocker zuerst reduzieren" : "Blockerfreie Cases zuerst entscheiden"}</strong>
              <div>
                {analyticsSnapshot.blockerCases
                  ? `${analyticsSnapshot.blockerCases} Cases sind aktuell nicht freigabereif. Dokumente und Filing-Readiness sind zuerst zu haerten.`
                  : "Der Arbeitsraum hat aktuell keine blockernden Filing-Signale im geladenen Fokus."}
              </div>
            </CCallout>
            <div className="d-grid gap-2">
              <CButton color="secondary" variant="outline" onClick={() => navigate(`/dashboard/employer/compliance${workspace.selectedAdId ? `?adId=${workspace.selectedAdId}${workspace.isTraining ? "&training=1" : ""}` : ""}`)}>
                Filing & Compliance
              </CButton>
              <CButton color="secondary" variant="outline" onClick={() => navigate(`/dashboard/employer/analytics${workspace.selectedAdId ? `?adId=${workspace.selectedAdId}${workspace.isTraining ? "&training=1" : ""}` : ""}`)}>
                Analytics
              </CButton>
              <CButton color="secondary" variant="outline" onClick={() => navigate(`/dashboard/employer/integration${workspace.selectedAdId ? `?adId=${workspace.selectedAdId}${workspace.isTraining ? "&training=1" : ""}` : ""}`)}>
                Integration
              </CButton>
            </div>
          </section>
        </aside>
      </div>
    </EmployerPageFrame>
  );
}
